import { Camera, Scene, SRGBColorSpace, Vector2 } from "three";
import { WebGPURenderer } from "three/webgpu";
import { IRenderer } from "./IRenderer";
import { GPU_POWER_PREFERENCE, logActiveWebGPUGpu } from "./logActiveGpu";
import { renderSettingsStore } from "../../core/pipeline/RenderSettingsStore";

export class WebGPURendererSingleton implements IRenderer {
  private _render_instance: WebGPURenderer;
  private static _instance: WebGPURendererSingleton;
  private readonly _initPromise: Promise<void>;
  private _ready = false;
  private _size: Vector2 = new Vector2();
  /**
   * The GPU context was lost (GPU memory, driver reset). three never draws
   * with this renderer again, so `getInstance()` makes a new one.
   */
  private _lost = false;
  private readonly _lostListeners = new Set<() => void>();

  private constructor() {
    this._render_instance = new WebGPURenderer({
      alpha: true,
      antialias: false,
      powerPreference: GPU_POWER_PREFERENCE,
      forceWebGL: true,
      // Defaults are 8 storage buffers and workgroup 256. Surfel GI needs 10
      // and 512; request only those (not 1024 — that failed device init here).
      requiredLimits: {
        maxStorageBuffersPerShaderStage: 10,
        maxComputeWorkgroupSizeX: 512,
        maxComputeInvocationsPerWorkgroup: 512,
      },
    });
    this._initPromise = this._initialize();
    // MSAA RT + 0.5 pixelRatio looks blocky; AA is FXAA via render settings.
    Reflect.set(this._render_instance, "_samples", 0);
    const threeOnDeviceLost = this._render_instance.onDeviceLost.bind(this._render_instance);
    this._render_instance.onDeviceLost = (info) => {
      threeOnDeviceLost(info);
      this._lost = true;
      this._lostListeners.forEach((listener) => listener());
    };
  }

  /** True after the GPU context was lost; this renderer stays blank for good. */
  get lost(): boolean {
    return this._lost;
  }

  /** Called once when the GPU context is lost. Returns the unsubscribe. */
  onLost(listener: () => void): () => void {
    this._lostListeners.add(listener);
    return () => this._lostListeners.delete(listener);
  }

  private async _initialize(): Promise<void> {
    await this._render_instance.init();
    this._render_instance.setPixelRatio(renderSettingsStore.resolvePixelRatio());
    this._render_instance.outputColorSpace = SRGBColorSpace;
    renderSettingsStore.applyRenderer(this._render_instance);
    this._ready = true;
    logActiveWebGPUGpu(this._render_instance);
  }

  async ensureReady(): Promise<void> {
    await this._initPromise;
  }

  get isWebGPUBackend(): boolean {
    const backend = (
      this._render_instance as WebGPURenderer & {
        backend?: { isWebGPUBackend?: boolean };
      }
    ).backend;
    return backend?.isWebGPUBackend === true;
  }

  get isWebGPURenderer(): boolean {
    return true;
  }

  get supportsClassicPostProcessing(): boolean {
    return false;
  }

  render(scene: Scene, camera: Camera): void {
    this.instance.render(scene, camera);
  }

  get instance(): WebGPURenderer {
    return this._render_instance;
  }

  screenshot(type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.instance.domElement.toBlob(
        (blob: Blob | null) => {
          resolve(blob);
        },
        type,
        quality,
      );
    });
  }

  get size(): Vector2 {
    this._render_instance.getSize(this._size);
    return this._size;
  }

  get ready(): boolean {
    return this._ready;
  }

  public static getInstance(): WebGPURendererSingleton {
    // A lost renderer never draws again: the next Engine gets a new one.
    if (!WebGPURendererSingleton._instance || WebGPURendererSingleton._instance._lost) {
      WebGPURendererSingleton._instance = new WebGPURendererSingleton();
    }
    return WebGPURendererSingleton._instance;
  }
}
